/**
 * @fileoverview Hook para detectar células editadas na DRE
 * @module hooks/useCellEditStatus
 * 
 * @description
 * Hook que identifica quais células foram editadas manualmente
 * e retorna informações de comparação com valores originais.
 * 
 * @version 1.0.0
 * @date 2025-10-21
 */

import { useMemo } from 'react';
import { useDREIntegration } from './useDREIntegration';

export interface CellEditInfo {
  isEdited: boolean;
  originalValue: number | null;
  currentValue: number;
  difference: number;
  percentageChange: number;
}

// Campos numéricos editáveis da DRE
type DRENumericField = 'receitaBruta' | 'impostosVendas' | 'receitaLiquida' | 'custosDirectos' | 
  'lucroBruto' | 'despesasOperacionais' | 'ebitda' | 'depreciacao' | 'ebit' | 'impostosLucro' | 'lucroLiquido';

/**
 * Hook que retorna informações sobre edições em células da DRE
 * 
 * @returns Função para verificar se célula foi editada
 * 
 * @example
 * ```tsx
 * function TabelaDRE() {
 *   const getCellEditInfo = useCellEditStatus();
 *   
 *   const editInfo = getCellEditInfo('setima', 2024, 'receitaBruta');
 *   if (editInfo.isEdited) {
 *     return <EditedCell info={editInfo} />;
 *   }
 * }
 * ```
 */
export function useCellEditStatus() {
  const { dreData, masterData, hasEdits } = useDREIntegration();

  return useMemo(() => {
    /**
     * Verifica se uma célula específica foi editada
     */
    return function getCellEditInfo(
      empresa: 'setima' | 'studio555',
      year: number,
      field: DRENumericField | string // Aceita string para compatibilidade
    ): CellEditInfo {
      // Se não há edições, retornar não editado
      if (!hasEdits || !dreData || !masterData) {
        const currentYear = dreData?.[empresa].years.find(y => y.year === year);
        return {
          isEdited: false,
          originalValue: null,
          currentValue: (currentYear as unknown as Record<string, unknown>)?.[field] as number || 0,
          difference: 0,
          percentageChange: 0,
        };
      }

      // Buscar ano nos dados editados e originais
      const currentYear = dreData[empresa].years.find(y => y.year === year);
      const originalYear = masterData[empresa].years.find(y => y.year === year);

      if (!currentYear) {
        return {
          isEdited: false,
          originalValue: null,
          currentValue: 0,
          difference: 0,
          percentageChange: 0,
        };
      }

      const currentValue = (currentYear as unknown as Record<string, unknown>)[field] as number;
      const originalValue = (originalYear as unknown as Record<string, unknown>)?.[field] as number || null;

      // Se não há valor original, não é editado
      if (originalValue === null) {
        return {
          isEdited: false,
          originalValue: null,
          currentValue,
          difference: 0,
          percentageChange: 0,
        };
      }

      // Calcular diferença
      const difference = currentValue - originalValue;
      const isEdited = Math.abs(difference) > 0.01; // Tolerância para floating point

      // Calcular variação percentual
      const percentageChange = originalValue !== 0 
        ? (difference / originalValue) * 100 
        : 0;

      return {
        isEdited,
        originalValue,
        currentValue,
        difference,
        percentageChange,
      };
    };
  }, [dreData, masterData, hasEdits]);
}
